import { useState } from "react";
import axios from "axios";

const initialCaseStudyForm = {
  title: "",
  slug: "",
  headline: "",
  subheadline: "",
  category: "",
  client: "",
  industry: "",
  timeline: "",
  services: "",
  challenge: "",
  approach: "",
  results: [{ value: "", detail: "", label: "", timeframe: "" }],
  techStack: "",
  testimonial: "",
  testimonialClient: "",
  testimonialRole: "",
  testimonialInitials: "",
  thumbnailUrl: "",
  siteUrl: "",
};

const useCaseStudy = (apiUrl, effectiveId) => {
  const [caseStudyModalOpen, setCaseStudyModalOpen] = useState(false);
  const [editingCaseStudyIndex, setEditingCaseStudyIndex] = useState(null);
  const [caseStudyForm, setCaseStudyForm] = useState(initialCaseStudyForm);
  const [savingCaseStudy, setSavingCaseStudy] = useState(false);

  const openCaseStudyModal = (index, existingData = null) => {
    setEditingCaseStudyIndex(index);
    if (existingData) {
      // Edit mode - existing data load karein
      setCaseStudyForm({
        ...initialCaseStudyForm,
        title: existingData.title || "",
        slug: existingData.slug || "",
        headline: existingData.headline || "",
        subheadline: existingData.subheadline || "",
        category: existingData.category || "",
        siteUrl: existingData.siteUrl || "",
        thumbnailUrl: existingData.thumbnailUrl || "",
      });
    } else {
      setCaseStudyForm(initialCaseStudyForm);
    }
    setCaseStudyModalOpen(true);
  };

  const closeCaseStudyModal = () => {
    setCaseStudyModalOpen(false);
    setEditingCaseStudyIndex(null);
    setCaseStudyForm(initialCaseStudyForm);
  };

  const handleAddCaseStudyToPage = async (setForm, setModalDraft) => {
    setSavingCaseStudy(true);
    try {
      const payload = {
        ...caseStudyForm,
        techStack: caseStudyForm.techStack
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        isPublished: true,
      };

      // 1. Case Study create karein
      const res = await axios.post(`${apiUrl}/case-studies/create`, payload);
      const saved = res.data.success ? res.data.data : res.data;

      if (!saved) {
        throw new Error("No data received from server");
      }

      console.log("✅ Case study saved:", saved);

      // 2. New item object banayein
      const newCaseStudyItem = {
        caseStudyId: saved._id,
        slug: saved.slug,
        title: saved.title,
        category: saved.category,
        siteUrl: saved.siteUrl,
        thumbnailUrl: saved.thumbnailUrl,
      };

      // 3. Local state update karein
      if (setForm) {
        setForm((prev) => {
          const items = [...(prev.caseStudies.items || [])];
          items[editingCaseStudyIndex] = {
            ...items[editingCaseStudyIndex],
            ...newCaseStudyItem,
          };
          return {
            ...prev,
            caseStudies: { ...prev.caseStudies, items },
          };
        });
      }

      // 4. Modal draft update karein
      if (setModalDraft) {
        setModalDraft((prev) => {
          if (!prev) return prev;
          const items = [...(prev.items || [])];
          items[editingCaseStudyIndex] = {
            ...items[editingCaseStudyIndex],
            ...newCaseStudyItem,
          };
          return { ...prev, items };
        });
      }

      // 5. ⭐ IMPORTANT: Agar page already saved hai toh backend par bhi update karein
      if (effectiveId) {
        try {
          // Current form state lein
          const currentForm = setForm ? await getCurrentForm() : null;

          if (currentForm) {
            const items = [...(currentForm.caseStudies.items || [])];
            items[editingCaseStudyIndex] = {
              ...items[editingCaseStudyIndex],
              ...newCaseStudyItem,
            };

            await axios.put(`${apiUrl}/pages/${effectiveId}`, {
              ...currentForm,
              caseStudies: {
                ...currentForm.caseStudies,
                items,
              },
            });
            console.log("✅ Page updated with case study");
          }
        } catch (updateError) {
          console.error(
            "⚠️ Page update failed (case study still saved):",
            updateError,
          );
        }
      }

      closeCaseStudyModal();
    } catch (error) {
      console.error("❌ Error saving case study:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save case study";
      alert(errorMessage);
    } finally {
      setSavingCaseStudy(false);
    }
  };

  // Helper function to get current form state
  const getCurrentForm = async () => {
    if (!effectiveId) return null;
    try {
      const res = await axios.get(`${apiUrl}/pages/id/${effectiveId}`);
      return res.data.success ? res.data.data : null;
    } catch (error) {
      console.error("Error fetching current form:", error);
      return null;
    }
  };

  return {
    caseStudyModalOpen,
    editingCaseStudyIndex,
    caseStudyForm,
    setCaseStudyForm,
    savingCaseStudy,
    openCaseStudyModal,
    closeCaseStudyModal,
    handleAddCaseStudyToPage,
  };
};

export default useCaseStudy;
